import { type Ref, useMemo } from 'react'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import {
  CustomSelect,
  type RenderSearchItemProps,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { observer } from 'mobx-react-lite'
import {
  type TransferJobDialogViewModel,
  type TransferJobOption,
  transferReasons,
} from './TransferJobDialogViewModel'
import { match } from '@/utils/pattern-match-util'
import { ClientInputEditorContainer } from '@/features/jobs/TransferJobDialog/ClientInputEditor/ClientInputEditorContainer'
import { ProInputEditorContainer } from '@/features/jobs/TransferJobDialog/ProInputEditor/ProInputEditorContainer'
import { ClientTeamInputEditorContainer } from '@/features/jobs/TransferJobDialog/ClientTeamInputEditor/ClientTeamInputEditorContainer'
import { useConfirmOnPageExit } from '@/hooks/dom-hooks'
import { TransferJobDialog } from '@/features/jobs/TransferJobDialog'

export interface TransferJobDialogContainerProps {
  vm: TransferJobDialogViewModel
}

export const TransferJobDialogContainer = observer(
  function TransferJobDialogContainer({ vm }: TransferJobDialogContainerProps) {
    const itemSource = useMemo(
      () => ItemSource.static(vm.filteredTransferOptions),
      [vm.filteredTransferOptions],
    )

    const selectProps = useCustomSelectStateFromItemSource({ itemSource })

    useConfirmOnPageExit(vm.transfer.pending)

    return (
      <TransferJobDialog
        showing={vm.modal.showing}
        jobCount={vm.jobs.length}
        optionField={
          <FormGroupContainer label="Transfer to">
            <CustomSelect
              {...selectProps}
              value={vm.option}
              itemToString={(i) => i.displayName}
              multi={false}
              onChange={vm.setTransferOption}
              renderSearchItem={(itemProps) => (
                <TransferSearchItem {...itemProps} />
              )}
              testId="transfer-job-type-dropdown"
            />
          </FormGroupContainer>
        }
        transferEditor={match(vm.transferType, {
          Pool: () => null,
          None: () => null,
          Team: () => (
            <ClientTeamInputEditorContainer vm={vm.transferViewModels.Team} />
          ),
          Pro: () => <ProInputEditorContainer vm={vm.transferViewModels.Pro} />,
          Client: () => (
            <ClientInputEditorContainer vm={vm.transferViewModels.Client} />
          ),
        })}
        reasonField={
          <SelectContainer
            model={vm}
            field="reasonOption"
            name="reasonOption"
            label="Reason"
            help={vm.reasonHelpText}
            items={transferReasons.map((r) => [r])}
            placeholder="Select a reason"
            error={vm.validation.getError('reasonOption')}
            data-test="transfer-job-reason-select"
          />
        }
        otherReasonField={
          vm.isOtherReason && (
            <TextAreaContainer
              model={vm}
              field="otherReason"
              name="otherReason"
              label="Other reason"
              isDisabled={false}
              error={vm.validation.getError('otherReason')}
              data-test="transfer-job-reason-input"
              rows={1}
            />
          )
        }
        showRateProButton={vm.showRateProButton}
        isTransferPending={vm.transfer.pending}
        isTransferRejected={vm.transfer.rejected}
        isTransferDisabled={vm.isTransferDisabled}
        transferError={vm.transfer.error}
        onRatePro={vm.showProRatingDialog}
        onClose={vm.modal.close}
        onTransfer={vm.transfer}
      />
    )
  },
)

interface TransferSearchItemProps extends RenderSearchItemProps<TransferJobOption> {
  ref?: Ref<HTMLDivElement>
}

function TransferSearchItem({
  item,
  itemToString: _itemToString,
  ref,
  ...itemProps
}: TransferSearchItemProps) {
  return (
    <SearchResultItem
      ref={ref}
      {...itemProps}
      testId={'transfer-job-type-option-' + item.transferType.toLowerCase()}
    >
      {item.displayName}
    </SearchResultItem>
  )
}
